import { createSelector } from '@reduxjs/toolkit';
import { selectTargetOffer } from '@sravni/package-select-mpl-offer/utils/select-target-offer';

import type { SCENARIO_STEP } from 'commonTypes/api/scenarios/BaseTypes';
import type { ContentOfType } from 'commonTypes/api/scenarios/scenario';

import { sendSentryClientErrorOnce } from 'shared/lib';
import type { SliceStateFromReducer } from 'shared/types';

import type { conditionsSelectionSlice } from './conditionsSelectionSlice';

export type TConditionsSelectionState = SliceStateFromReducer<typeof conditionsSelectionSlice>;

const selectConditionOffers = (state: Store) =>
  (state.scenario.currentStep?.content as ContentOfType<SCENARIO_STEP.CONDITIONS_SELECTION>)?.offers || [];

const selectConditionStep = (state: Store) => state.scenario.currentStep?.step;
const selectConditionTerm = (state: Store) => state.conditionsSelection.term;
const selectConditionInsurance = (state: Store) => state.conditionsSelection.insurance;

export const selectTargetOfferSelector = createSelector(
  [selectConditionOffers, selectConditionTerm, selectConditionInsurance, selectConditionStep],
  (offers, term, insurance, step) => {
    try {
      const targetOffer = selectTargetOffer(offers, term, insurance);

      const hasFilteredOffer = targetOffer?.filteredOffer && Object.keys(targetOffer.filteredOffer).length > 0;

      if (offers.length > 0 && !hasFilteredOffer) {
        sendSentryClientErrorOnce(
          `conditions_selection_empty_target_offer_${step}_${offers.length}`,
          'CONDITIONS_SELECTION - selectTargetOffer returned empty result',
          {
            step,
            offersCount: offers.length,
            term,
            insurance,
            offerIds: offers.map((offer) => offer?.uuid).filter(Boolean),
          },
          { level: 'error' },
        );
      }

      return targetOffer;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('No suitable offers');
    }
  },
);

export const getInsurance = (state: TConditionsSelectionState) => state.conditionsSelection.insurance;
export const getIsShowInsuranceModal = (state: TConditionsSelectionState) =>
  state.conditionsSelection.isShowInsuranceModal;

export const getOfferImprovementValue = (key: string) => (state: TConditionsSelectionState) =>
  state.conditionsSelection.offerImprovements[key] || false;

export const getEnabledOfferImprovements = (state: TConditionsSelectionState) =>
  Object.entries(state.conditionsSelection.offerImprovements)
    .filter(([_, value]) => value)
    .map(([code]) => code);

export const getIsShowGracePeriodInfoModal = (state: TConditionsSelectionState) =>
  state.conditionsSelection.isShowGracePeriodInfoModal;

export const getIsShowActiveOfferConflictModal = (state: TConditionsSelectionState) =>
  state.conditionsSelection.isShowActiveOfferConflictModal;
